import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    PersonneMorale,
    getPersonneMoraleById,
} from '@app/api/data/personne-morales';
import {
    Card,
    Typography,
    Divider,
    Button,
    Box,
    Alert,
    Sheet,
    Stack,
    Grid,
} from '@mui/joy';
import ParrainagesPersonneMorale from './ParrainagesPersonneMorale';
import ReassignParrainages from './ReassignParrainages';
import { AppIcon } from '@app/components/AppIcon';
import AppLoading from '@app/components/AppLoading';

const PersonneMoraleDetails: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [personneMorale, setPersonneMorale] = useState<PersonneMorale | null>(
        null
    );
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const adresse = personneMorale?.adresse;

    const fetchPersonneMorale = useCallback(async () => {
        if (!id) {
            setError('Identifiant manquant');
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const response = await getPersonneMoraleById(id);
            if (response?.data) {
                setPersonneMorale(response.data);
            } else {
                throw new Error('Aucune donnée reçue');
            }
        } catch (err: any) {
            console.error('Erreur lors du chargement:', err);
            setError(
                err?.message || 'Erreur lors du chargement des informations'
            );
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchPersonneMorale();
    }, [fetchPersonneMorale]);

    const handleBack = () => {
        navigate('/tables/data/personneMorales');
    };

    if (loading) {
        return (
            <Sheet className="h-40vh flex items-center justify-center">
                <AppLoading />
            </Sheet>
        );
    }

    if (error) {
        return (
            <Box className="p-3">
                <Alert color="danger" className="mb-2">
                    {error}
                </Alert>
                <Button onClick={fetchPersonneMorale} className="mr-1">
                    Réessayer
                </Button>
                <Button
                    size="sm"
                    variant="soft"
                    color="primary"
                    onClick={handleBack}
                    startDecorator={<AppIcon icon="ArrowLeftIcon" />}
                >
                    Retour à la liste
                </Button>
            </Box>
        );
    }

    if (!personneMorale) {
        return (
            <Box className="p-3">
                <Alert color="warning">
                    Aucune personne morale trouvée avec cet identifiant
                </Alert>
                <Button
                    size="sm"
                    variant="soft"
                    color="primary"
                    onClick={handleBack}
                    startDecorator={<AppIcon icon="ArrowLeftIcon" />}
                    className="mt-2"
                >
                    Retour à la liste
                </Button>
            </Box>
        );
    }

    return (
        <Box className="p-2">
            <Card variant="outlined" className="mb-3">
                <Box className="mb-2 flex items-center justify-between">
                    <Typography level="h3">
                        {personneMorale.raisonSociale}
                    </Typography>
                    <Button
                        size="sm"
                        variant="soft"
                        color="primary"
                        onClick={handleBack}
                        startDecorator={<AppIcon icon="ArrowLeftIcon" />}
                    >
                        Retour à la liste
                    </Button>
                </Box>

                <Divider />

                <Grid container spacing={2} className="mt-2">
                    <Grid xs={12} md={6}>
                        <Card variant="soft">
                            <Typography level="title-md" className="mb-1">
                                Informations générales
                            </Typography>
                            <Divider className="my-1" />

                            <Stack spacing={1} className="mt-2 w-full">
                                <div className="grid grid-cols-2 gap-2">
                                    <div className="col-span-1">
                                        <Box>
                                            <Typography level="body-xs" color="neutral">
                                                Téléphone:
                                            </Typography>
                                            <Typography>
                                                {personneMorale.telephone ||
                                                    'Non renseigné'}
                                            </Typography>
                                        </Box>

                                        <Box>
                                            <Typography level="body-xs" color="neutral">
                                                Email:
                                            </Typography>
                                            <Typography>
                                                {personneMorale.email ||
                                                    'Non renseigné'}
                                            </Typography>
                                        </Box>

                                        <Box>
                                            <Typography level="body-xs" color="neutral">
                                                Statut:
                                            </Typography>
                                            <Typography>
                                                {personneMorale.status}
                                            </Typography>
                                        </Box>
                                    </div>
                                    <div className="col-span-1">

                                        <Box>
                                            <Typography level="body-xs" color="neutral">
                                                Adresse:
                                            </Typography>
                                            <Typography>
                                                {adresse?.ligne1 || 'Non renseigné'}
                                            </Typography>
                                        </Box>

                                        <Box>
                                            <Typography level="body-xs" color="neutral">
                                                Ville:
                                            </Typography>
                                            <Typography>
                                                {adresse?.ville || 'Non renseigné'}
                                            </Typography>
                                        </Box>

                                        <Box>
                                            <Typography level="body-xs" color="neutral">
                                                Case postale:
                                            </Typography>
                                            <Typography>
                                                {adresse?.casePostal || 'Non renseigné'}
                                            </Typography>
                                        </Box>

                                        <Box>
                                            <Typography level="body-xs" color="neutral">
                                                NPA:
                                            </Typography>
                                            <Typography>
                                                {adresse?.NPA || 'Non renseigné'}
                                            </Typography>
                                        </Box>

                                        <Box>
                                            <Typography level="body-xs" color="neutral">
                                                Région:
                                            </Typography>
                                            <Typography>
                                                {adresse?.region || 'Non renseigné'}
                                            </Typography>
                                        </Box>

                                    </div>
                                </div>
                                {personneMorale.notes && (
                                    <Box className="mt-2">
                                        <Typography
                                            level="body-xs"
                                            color="neutral"
                                        >
                                            Notes:
                                        </Typography>
                                        <Typography>
                                            {personneMorale.notes}
                                        </Typography>
                                    </Box>
                                )}
                            </Stack>
                        </Card>
                    </Grid>
                </Grid>
            </Card>

            {/* Composant pour réaffecter des parrainages */}
            <ReassignParrainages personneMorale={personneMorale} />

            {/* Composant pour afficher les parrainages */}
            <ParrainagesPersonneMorale personneMorale={personneMorale} />
        </Box>
    );
};

export default PersonneMoraleDetails;
